import { NgFor, NgIf } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { marked } from "@enersis/gp-components/gp-markdown";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { DialogFrameModule } from "src/app/common/components/dialog-frame/dialog-frame.module";
import { CheckboxModule } from "src/app/common/components/form/checkbox/checkbox.module";
import { IconModule } from "src/app/shared/ui/icon/icon.module";
import { ICFPolicy } from "src/app/common/interfaces/app/policy";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { AuthService } from "src/app/core/services/auth.service";
import { updatePolicy } from "src/app/ngrx/actions/app.actions";
import { selectCurrentPolicy, selectUserInfo } from "src/app/ngrx/selectors/app.selectors";
import { TabConfig, TabsComponent } from "src/app/shared/ui/tabs/tabs.component";

export type PrivacyAndTermsTab = "approval" | "terms" | "dataProtection" | "cookies";

interface CheckboxConfig {
  name: string;
  label: string;
}

const CONTENT_MAP = {
  approval: () => "PRIVACY_AND_TERMS_DIALOG.approval.content",
  terms: (content: ICFPolicy) => marked(content.termsOfService),
  dataProtection: (content: ICFPolicy) => marked(content.privacyPolicy),
  cookies: () => "PRIVACY_AND_TERMS_DIALOG.cookies.content"
};

const TABS: Array<TabConfig<PrivacyAndTermsTab>> = [
  { name: "approval", label: "PRIVACY_AND_TERMS_DIALOG.tab.approval" },
  { name: "terms", label: "PRIVACY_AND_TERMS_DIALOG.tab.terms" },
  { name: "dataProtection", label: "PRIVACY_AND_TERMS_DIALOG.tab.dataProtection" },
  { name: "cookies", label: "PRIVACY_AND_TERMS_DIALOG.tab.cookies" }
];

const POLICY_CHECKBOXES: Array<string> = ["termsAgreements", "cookiesAgreements"];

const COOKIES_DETAILS_CHECKBOXES: Array<CheckboxConfig> = [
  { name: "functional", label: "PRIVACY_AND_TERMS_DIALOG.cookies.functional" },
  { name: "statistic", label: "PRIVACY_AND_TERMS_DIALOG.cookies.statistic" },
  { name: "comfort", label: "PRIVACY_AND_TERMS_DIALOG.cookies.comfort" }
];

const POLICY_LINKS_MAP: Record<string, { label: string; link: PrivacyAndTermsTab }> = {
  termsAgreements: {
    label: "PRIVACY_AND_TERMS_DIALOG.termsAgreements.link",
    link: "terms"
  },
  cookiesAgreements: {
    label: "PRIVACY_AND_TERMS_DIALOG.cookiesAgreements.link",
    link: "cookies"
  }
};

@Component({
  selector: "tpm-privacy-and-terms",
  templateUrl: "privacy-and-terms.component.html",
  styleUrls: ["privacy-and-terms.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    TranslateModule,
    DialogFrameModule,
    ReactiveFormsModule,
    CheckboxModule,
    IconModule,
    TabsComponent
  ]
})
export class PrivacyAndTermsDialogComponent implements AfterViewInit {
  private readonly dialogRef = inject(MatDialogRef<PrivacyAndTermsDialogComponent>);
  private readonly dialogData = inject<{ isFirstVisit: boolean; tab?: PrivacyAndTermsTab }>(MAT_DIALOG_DATA);

  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly analytics = inject(AnalyticsService);
  private readonly authService = inject(AuthService);

  private readonly currentPolicy = toSignal(this.store.select(selectCurrentPolicy));
  private readonly userInfo = toSignal(this.store.select(selectUserInfo));

  readonly policyCheckboxes: Array<string> = POLICY_CHECKBOXES;
  readonly cookiesDetailsCheckboxes: Array<CheckboxConfig> = COOKIES_DETAILS_CHECKBOXES;
  readonly policyLinks: Record<string, { label: string; link: PrivacyAndTermsTab }> = POLICY_LINKS_MAP;

  readonly hasFooter = computed(() => this.activeTab() === "approval" || this.activeTab() === "cookies");
  readonly content = computed(() => (this.currentPolicy() ? CONTENT_MAP[this.activeTab()](this.currentPolicy()) : ""));

  readonly hasCloseButton = signal<boolean>(!this.dialogData.isFirstVisit);
  readonly activeTab = signal<PrivacyAndTermsTab | "approval">("approval");
  readonly tabs: Array<TabConfig> = TABS;

  readonly submitButtonLabel = computed(() =>
    this.activeTab() === "cookies"
      ? "PRIVACY_AND_TERMS_DIALOG.cookiesButtonLabel"
      : "PRIVACY_AND_TERMS_DIALOG.submitButtonLabel"
  );

  readonly form = this.fb.group({
    version: ["v2", Validators.required],
    termsAgreements: [{ value: true, disabled: true }, Validators.requiredTrue],
    cookiesAgreements: [false, Validators.requiredTrue],
    cookiesDetails: this.fb.group({
      functional: [{ value: true, disabled: true }, Validators.requiredTrue],
      statistic: [false],
      comfort: [false]
    })
  });

  constructor() {
    if (this.dialogData.isFirstVisit) {
      this.disableClose();
    } else {
      this.activeTab.set(this.dialogData.tab ?? TABS[0].name);
      this.form.patchValue(this.authService.getPolicyCookies());
      this.cookiesDetailsGroup.markAsDirty();
    }

    this.cookiesAgreementsControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (this.cookiesDetailsGroup.pristine) {
        this.cookiesDetailsGroup.patchValue({
          statistic: value,
          comfort: value
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.analytics.scanForForms();
  }

  get cookiesAgreementsControl(): AbstractControl {
    return this.form.get("cookiesAgreements") as AbstractControl;
  }

  get cookiesDetailsGroup(): AbstractControl {
    return this.form.get("cookiesDetails") as AbstractControl;
  }

  get submitDisabled(): boolean {
    return this.activeTab() === "cookies" ? false : this.form.invalid;
  }

  get isAllCookiesDetailsChecked(): boolean {
    return this.cookiesDetailsGroup.pristine || Object.values(this.cookiesDetailsGroup.getRawValue()).every(Boolean);
  }

  getCheckboxLabel(name: string): string {
    if (name === "cookiesAgreements") {
      return this.isAllCookiesDetailsChecked
        ? "PRIVACY_AND_TERMS_DIALOG.cookiesAgreements.allCookiesLabel"
        : "PRIVACY_AND_TERMS_DIALOG.cookiesAgreements.adjustSettingsLabel";
    }
    return "PRIVACY_AND_TERMS_DIALOG.termsAgreements.label";
  }

  onSubmit(): void {
    if (this.activeTab() === "cookies") {
      this.toApprovalTab();
    } else {
      this.saveCookies();
    }
  }

  toApprovalTab(): void {
    this.activeTab.set("approval");
  }

  saveCookies(): void {
    if (this.form.valid) {
      const { statistic } = this.form.getRawValue().cookiesDetails;

      if (statistic) {
        this.analytics.activate(this.userInfo());
      } else {
        this.analytics.deactivate();
      }

      this.store.dispatch(
        updatePolicy({ policy: { ...this.form.getRawValue(), version: this.currentPolicy().version } })
      );
      this.dialogRef.close();
    }
  }

  private disableClose(): void {
    this.dialogRef.disableClose = true;
  }
}
